<template>
  <div class="padded" style="text-align: left;">

    <filter-bar
      v-if="!hideTop"
      :title="title"
      :icon="icon"
      :search-placeholder="searchPlaceholder"
      :show-search="showSearch"
      :show-refresh="showRefresh"
      :hide-org-badge="hideOrgBadge"
      :title-heading="titleHeading"
      :title-class="titleClass"
      :table-loading="loadingEvent"
      :table-loaded="loadedEvent"
      :set-filter-event="filterEvent"
      :reset-filter-event="filterReset"
      :refresh-event="refreshTable">
      <!-- Forward the `actions` slot into filter-bar so add-buttons share the
           same row as title + search. Keeps the `<hr />` below at full width. -->
      <template v-if="$slots.actions" #actions>
        <slot name="actions" />
      </template>
    </filter-bar>

    <!-- `below-filter` slot: sits between the filter-bar and the data rows.
         Mirrors the legacy vuetable-2 `bottom-comp` placement; used by
         SitesTable for the SiteMenu chip strip. -->
    <slot name="below-filter" />

    <el-table
      v-loading="isLoading && !rows.length"
      :ref="tableRef"
      :data="rows"
      :row-key="trackBy"
      stripe
      :empty-text="''"
      @sort-change="onSortChange"
      @selection-change="onSel">

      <!-- Leading selection column: opt-in via :selectable="true". Renders a
           checkbox column (type="selection") with reserve-selection so checked
           rows survive page turns. row-key (trackBy) is required by Element UI
           for reserve-selection to work correctly. -->
      <el-table-column v-if="selectable" type="selection" width="48" :reserve-selection="true" />

      <!-- Leading chevron column: appears whenever a layer route or layer component is wired.
           Mirrors the vuetable-2 expand-row button so users muscle-memoried on
           the chevron keep working alongside the new Site-ID layer link. -->
      <el-table-column v-if="layerRoute || layerComponent" label="" width="70" align="center" class-name="xolas-table__chevron-col">
        <template slot-scope="scope">
          <el-button
            type="info"
            size="mini"
            plain
            round
            :aria-label="'Open detail for row ' + (scope.$index + 1)"
            @click="onRowClick(scope.row, $event)">
            <i class="fa fa-angle-right"></i>
          </el-button>
        </template>
      </el-table-column>

      <el-table-column
        v-for="field in visibleFields"
        :key="field.name"
        :prop="field.name.startsWith('__') ? undefined : field.name"
        :label="field.title"
        :sortable="field.sortField ? 'custom' : false"
        :class-name="field.dataClass"
        :label-class-name="field.titleClass"
        :width="field.width"
        :min-width="field.minWidth"
        :align="field.align">
        <template slot-scope="scope">
          <slot
            v-if="field.name.startsWith('__slot:')"
            :name="field.name.split(':')[1]"
            :row-data="scope.row"
            :row-index="scope.$index" />
          <span v-else-if="field.name === '__sequence'">{{ sequenceIndex(scope.$index) }}</span>
          <!-- eslint-disable-next-line vue/no-v-html -- callback output is developer-controlled HTML (vuetable-2 parity) -->
          <span v-else-if="field.callback" v-html="formatCell(field, scope.row)"></span>
          <a
            v-else-if="(layerRoute || layerComponent) && field === firstDataField"
            :href="resolveLayerHref(scope.row)"
            class="xolas-table__row-link"
            @click.prevent="onRowClick(scope.row, $event)"
            @mousedown="onRowMouseDown(scope.row, $event)">
            {{ formatCell(field, scope.row) }}
          </a>
          <span v-else>{{ formatCell(field, scope.row) }}</span>
        </template>
      </el-table-column>

      <el-table-column v-if="showEditButton || showDeleteButton" label="" width="120">
        <template slot-scope="scope">
          <el-button v-if="showEditButton" size="mini" @click="$emit('edit-click', scope.row)">Edit</el-button>
          <el-button v-if="showDeleteButton" type="danger" size="mini" @click="$emit('delete-click', scope.row)">Delete</el-button>
        </template>
      </el-table-column>

      <template #empty>
        <slot name="empty">
          <span class="text-muted">No records</span>
        </slot>
      </template>

    </el-table>

    <el-row v-if="showPagination && page.total > page.per_page">
      <el-col align="right" :span="24">
        <xolas-table-pagination
          ref="pagination"
          :page-data="page"
          @change-page="onChangePage" />
      </el-col>
    </el-row>

  </div>
</template>

<script>
import axios from '@/lib/axiosClient'
import ArrayMixins from '@/mixins/ArrayMixins'
import DateMixins from '@/mixins/DateMixins'
import FilterBar from '@/components/atoms/tables/DataTableFilterBar'
import XolasTablePagination from '@/components/atoms/tables/XolasTablePagination'
import { pushDrawer, resolveDrawerHref } from '@/lib/layerNav'

export default {
  name: 'xolas-table',

  mixins: [ArrayMixins, DateMixins],

  components: {
    FilterBar,
    XolasTablePagination
  },

  props: {
    url: {
      type: String,
      required: true
    },
    fields: {
      type: Array,
      required: true
    },
    title: {
      type: String,
      default: null
    },
    icon: {
      type: String,
      default: null
    },
    tableRef: {
      type: String,
      default: 'xolastable'
    },
    trackBy: {
      type: String,
      default: 'id'
    },
    sortField: {
      type: [String, Array],
      default: 'id'
    },
    sortDirection: {
      type: String,
      default: 'asc'
    },
    perPage: {
      type: Number,
      default: 10
    },
    searchPlaceholder: {
      type: String,
      default: 'Search...'
    },
    showSearch: {
      type: Boolean,
      default: true
    },
    showRefresh: {
      type: Boolean,
      default: true
    },
    // Legacy DataTable parity: suppress the <org-badge> prefix in the title
    // (e.g. FinanceSummary renders "Finance Statement", not "Xolas Finance
    // Statement"). Forwarded to DataTableFilterBar's existing hideOrgBadge.
    hideOrgBadge: {
      type: Boolean,
      default: false
    },
    // Legacy DataTable parity: the old wrapper defaulted its title heading to
    // h5 and forwarded it to DataTableFilterBar; without forwarding, the
    // filter bar's own h4 default made every migrated title one level larger
    // than its pre-migration render. Consumers that legacy-passed an explicit
    // title-heading (e.g. OrgTable's h2) carry it through this prop when they
    // migrate.
    titleHeading: {
      type: String,
      default: 'h5'
    },
    // Legacy DataTable parity: the old wrapper defaulted title-class to
    // 'text-info' (#909399); without it the heading falls back to the global
    // h5 color.
    titleClass: {
      type: String,
      default: 'text-info'
    },
    showPagination: {
      type: Boolean,
      default: true
    },
    hideTop: {
      type: Boolean,
      default: false
    },
    params: {
      type: Object,
      default: () => ({})
    },
    // layer wiring (legacy — deprecated in XOL-209 PR 2, use layerComponent instead)
    layerRoute: {
      type: String,
      default: null
    },
    layerParam: {
      type: String,
      default: 'id'
    },
    layerParamName: {
      type: String,
      default: 'id'
    },
    // layer wiring (XOL-209 PR 2 — d.<component>.<args> URL grammar)
    // layerComponent: registered drawer name (e.g. 'bill', 'site')
    layerComponent: {
      type: String,
      default: null
    },
    // layerArgs: rowData-key map per drawer arg (e.g. { id: 'id' })
    layerArgs: {
      type: Object,
      default: function () { return { id: 'id' } }
    },
    // category-B affordances — off by default in PR 1
    showEditButton: {
      type: Boolean,
      default: false
    },
    showDeleteButton: {
      type: Boolean,
      default: false
    },
    // opt-in multi-select: renders a leading checkbox column (type="selection").
    // When true, the el-table emits selection-change which XolasTable re-emits
    // as @selection-change. Use clearSelection() to programmatically deselect.
    selectable: {
      type: Boolean,
      default: false
    },
    // XOL-321 Phase B — select-all-with-exclusions.
    // autoSelectAll: when true, after each successful fetch() XolasTable
    // programmatically checks every row whose trackBy id is NOT in excludedIds.
    // The programmatic batch is wrapped in _suppressSelEmit so it does not
    // trigger @selection-change on the parent — only genuine user interactions do.
    // Default off: no behaviour change for existing consumers.
    autoSelectAll: {
      type: Boolean,
      default: false
    },
    // excludedIds: ids (values of the trackBy field) that should remain unchecked
    // during the autoSelectAll batch. Read only when autoSelectAll is true.
    excludedIds: {
      type: Array,
      default: function () { return [] }
    }
  },

  data () {
    return {
      rows: [],
      isLoading: true,
      page: {
        total: 0,
        current_page: 1,
        last_page: 1,
        per_page: this.perPage
      },
      filterText: '',
      sortOrder: [{ field: this.sortField, direction: this.sortDirection }],
      moreParams: Object.assign({}, this.params),
      // Current window width, used to drop responsive (`hidden-*`) columns at
      // their Element-UI breakpoints. Defaults wide so non-browser envs (jsdom)
      // keep every column; the real value is read in mounted (browser only).
      winWidth: 1920
    }
  },

  computed: {
    visibleFields () {
      return this.fields.filter(f => f.visible !== false && !this.isHiddenAtWidth(f))
    },

    firstDataField () {
      // First data field eligible for the layer-link <a>: skip __-prefixed
      // specials (e.g. __sequence, __slot:*). Callback fields are allowed —
      // they may format the value but are still valid click targets (e.g.
      // SiteUtilTable's 'date' field). Only the __-prefix check is applied.
      return this.fields.find(f => !f.name.startsWith('__'))
    },

    loadedEvent () {
      return this.tableRef + '-loaded'
    },

    loadingEvent () {
      return this.tableRef + '-loading'
    },

    refreshTable () {
      return this.tableRef + '-refresh-table'
    },

    filterEvent () {
      return this.tableRef + '-filter-set'
    },

    filterReset () {
      return this.tableRef + '-filter-reset'
    }
  },

  methods: {
    // selection-change handler: re-emit so parent can bind @selection-change.
    // Guard: while _suppressSelEmit is true (set during applyAutoSelection's
    // programmatic toggleRowSelection batch) do NOT propagate to the parent —
    // this prevents the feedback loop where auto-checking rows triggers
    // BulkRateChange.onSelectionChange and wrongly resets excludedIds.
    onSel (rows) {
      if (this._suppressSelEmit) return
      this.$emit('selection-change', rows)
    },

    // Programmatically clear all checked rows (delegates to el-table).
    clearSelection () {
      var t = this.$refs[this.tableRef]
      if (t && t.clearSelection) t.clearSelection()
    },

    // Returns the trackBy ids of all rows currently loaded on this page.
    // Used by BulkRateChange to page-scope the exclude/re-include reconcile
    // (XOL-321 Phase B) — only current-page ids are touched per reconcile call;
    // rows on other pages are left alone.
    currentRowIds () {
      var self = this
      return this.rows.map(function (r) { return r[self.trackBy] })
    },

    // XOL-321 Phase B — programmatically check/uncheck current-page rows to
    // match the autoSelectAll + excludedIds state.  Called after a successful
    // fetch() (via $nextTick) and on excludedIds/autoSelectAll changes.
    // Feedback-loop containment: _suppressSelEmit is set true for the entire
    // batch so onSel() is a no-op during the toggle calls; cleared on the next
    // $nextTick after all toggles complete.
    applyAutoSelection () {
      if (!this.autoSelectAll) return
      var self = this
      var t = self.$refs[self.tableRef]
      if (!t || typeof t.toggleRowSelection !== 'function') return

      var excludedSet = {}
      ;(self.excludedIds || []).forEach(function (id) { excludedSet[id] = true })

      self._suppressSelEmit = true
      self.rows.forEach(function (row) {
        var id = row[self.trackBy]
        t.toggleRowSelection(row, !excludedSet[id])
      })
      self.$nextTick(function () {
        self._suppressSelEmit = false
      })
    },

    // Re-run el-table's column-width layout pass. Called after mount and after
    // each successful data load so the table fills its container correctly even
    // when rendered inside a drawer/component-swap that measures too early.
    relayout () {
      var t = this.$refs[this.tableRef]
      if (t && typeof t.doLayout === 'function') {
        t.doLayout()
      }
    },

    // Whether a field carries an Element-UI `hidden-*` responsive class that
    // hides it at the current window width. el-table uses fixed table-layout
    // with a <colgroup>: a column merely CSS-hidden (display:none) still
    // reserves its <col> width, leaving dead space (notably an empty gap at the
    // right edge when the hidden column is last, e.g. Total Deposit). vuetable-2
    // used auto layout where hidden cells collapse. To match that — and the dev
    // render — we drop the column from `visibleFields` entirely at the same
    // breakpoints the class encodes, so no width is reserved. Thresholds mirror
    // Element UI 2.x: xs<768, sm<992, md<1200, lg<1920, xl>=1920.
    isHiddenAtWidth (field) {
      var cls = (field.dataClass || '') + ' ' + (field.titleClass || '')
      if (cls.indexOf('hidden-') === -1) return false
      var w = this.winWidth
      if (/hidden-xs-only/.test(cls) && w < 768) return true
      if (/hidden-sm-and-down/.test(cls) && w < 992) return true
      if (/hidden-md-and-down/.test(cls) && w < 1200) return true
      if (/hidden-lg-and-down/.test(cls) && w < 1920) return true
      if (/hidden-xl-only/.test(cls) && w >= 1920) return true
      return false
    },

    fetch () {
      this.isLoading = true
      this.$events.emit(this.loadingEvent)

      const sortField = this.sortOrder[0] ? this.sortOrder[0].field : this.sortField
      const sortDir = this.sortOrder[0] ? this.sortOrder[0].direction : this.sortDirection

      const params = Object.assign({}, this.moreParams, {
        page: this.page.current_page,
        per_page: this.page.per_page,
        sort: sortField,
        sort_dir: sortDir
      })

      if (this.filterText) {
        params.filter = this.filterText
      }

      // Match legacy DataTable: resolve the API base + org scoping via the
      // global Vue mixin's getEndpoint(). Without this the request hits the
      // SPA origin (e.g. localhost:8080/sites) and returns index.html, which
      // parses to an empty data set with no error surfaced.
      const apiUrl = typeof this.getEndpoint === 'function'
        ? this.getEndpoint(this.url)
        : this.url

      axios.get(apiUrl, { params }).then(res => {
        const data = res.data
        // Normalise the paginator's `data` to an array. Some endpoints (e.g.
        // /report/property/due) return `data` as a JSON object instead of an
        // array on certain pages — a Laravel quirk when the paginated collection
        // has non-sequential keys. el-table's :data requires an array (it would
        // render zero rows for an object), whereas the legacy vuetable tolerated
        // objects via v-for. Coerce object → values so pagination works either way.
        const pageRows = data.data
        this.rows = Array.isArray(pageRows)
          ? pageRows
          : (pageRows && typeof pageRows === 'object' ? Object.values(pageRows) : [])
        this.page = {
          total: data.total || 0,
          current_page: data.current_page || 1,
          last_page: data.last_page || 1,
          per_page: data.per_page || this.perPage
        }
        this.$emit('total-changed', this.page.total)
        this.isLoading = false
        this.$events.emit(this.loadedEvent)
        this.$emit('loaded', this.rows)
        // Re-run layout after rows are painted so column widths fill the
        // container correctly (fixes squished columns in drawer context).
        this.$nextTick(this.relayout)
        // XOL-321 Phase B: after rows are in the DOM, auto-check rows per the
        // excludedIds set. $nextTick ensures el-table has rendered the new rows
        // before toggleRowSelection is called on them.
        if (this.autoSelectAll) {
          this.$nextTick(this.applyAutoSelection)
        }
      }).catch(err => {
        this.isLoading = false
        this.$events.emit(this.loadedEvent)
        this.$emit('error', err)
      })
    },

    refresh () {
      this.fetch()
    },

    resolveLayerHref (rowData) {
      if (this.layerComponent) {
        const args = {}
        for (const [argName, rowKey] of Object.entries(this.layerArgs)) {
          args[argName] = rowData[rowKey]
        }
        return resolveDrawerHref(this.$router, { open: { [this.layerComponent]: args } })
      }
      // deprecated: layerRoute path
      if (!this.layerRoute) return '#'
      const resolved = this.$router.resolve({
        name: this.layerRoute,
        params: { [this.layerParamName]: rowData[this.layerParam] }
      })
      return resolved.href
    },

    onRowMouseDown (rowData, event) {
      if (!this.layerRoute && !this.layerComponent) return
      const isNewTab = event.metaKey || event.ctrlKey || event.button === 1
      if (isNewTab) {
        event.preventDefault()
        window.open(this.resolveLayerHref(rowData), '_blank')
      }
    },

    onRowClick (rowData, event) {
      this.$emit('row-click', rowData)
      if (this.layerComponent) {
        const args = {}
        for (const [argName, rowKey] of Object.entries(this.layerArgs)) {
          args[argName] = rowData[rowKey]
        }
        pushDrawer(this.$router, { open: { [this.layerComponent]: args } })
      } else if (this.layerRoute) {
        // deprecated: layerRoute path — kept for one PR cycle
        this.$router.push({
          name: this.layerRoute,
          params: { [this.layerParamName]: rowData[this.layerParam] }
        })
      }
    },

    onSortChange ({ column, prop, order }) {
      if (!prop) return
      this.sortOrder = [{
        field: prop,
        direction: order === 'ascending' ? 'asc' : 'desc'
      }]
      this.fetch()
    },

    onChangePage (page) {
      this.page.current_page = page
      this.fetch()
    },

    onFilterSet (filterText) {
      this.filterText = filterText
      this.page.current_page = 1
      this.fetch()
    },

    onFilterReset () {
      this.filterText = ''
      this.moreParams = Object.assign({}, this.params)
      this.page.current_page = 1
      this.fetch()
    },

    sequenceIndex (rowIndex) {
      // 1-based, page-aware (matches vuetable-2 __sequence semantics)
      const offset = (this.page.current_page - 1) * this.page.per_page
      return offset + rowIndex + 1
    },

    // Returns the count of rows currently loaded in this page.
    // Used by BulkRateChange to detect page-fully-selected for the
    // "select all N matching" banner (Phase B, XOL-321).
    currentRowCount () {
      return this.rows.length
    },

    formatCell (field, row) {
      // Resolve nested property path (e.g. 'tenancy.utilAccount')
      const value = field.name.split('.').reduce((obj, key) => {
        return obj && obj[key] !== undefined ? obj[key] : null
      }, row)

      if (!field.callback) return value

      if (typeof field.callback === 'function') {
        return field.callback(value)
      }

      if (typeof field.callback === 'string') {
        const parts = field.callback.split('|')
        const methodName = parts[0]
        const arg = parts[1]
        if (typeof this[methodName] === 'function') {
          return arg ? this[methodName](value, arg) : this[methodName](value)
        }
      }

      return value
    }
  },

  watch: {
    // Keep internal sortOrder in sync with reactive sort-field/sort-direction
    // props. data() initializes sortOrder ONCE; without this watcher the
    // fetch() fallback `this.sortOrder[0] ? ... : this.sortField` always picks
    // the stale initial sortOrder. RequestTable changes :sort-field on the fly
    // via its sort dropdown — this watcher makes that change actually take.
    // refresh() is intentionally NOT called here; consumers own when to refetch.
    sortField (val) {
      this.sortOrder = [{ field: val, direction: this.sortDirection }]
    },
    sortDirection (val) {
      this.sortOrder = [{ field: this.sortField, direction: val }]
    },
    // XOL-321 Phase B: re-apply auto-selection when excludedIds or autoSelectAll
    // changes (e.g. user re-includes a previously excluded row from the parent).
    // Suppression-guarded via applyAutoSelection itself.
    excludedIds () {
      if (this.autoSelectAll) this.applyAutoSelection()
    },
    autoSelectAll (val) {
      if (val) this.applyAutoSelection()
    }
  },

  created () {
    // XOL-321 Phase B: non-reactive instance field for the feedback-loop
    // suppression flag. Matches the _resizeObserver/_rafId pattern used for
    // the resize observer. Not in data() because reactivity is neither needed
    // nor desirable — the flag must be set/cleared synchronously around the
    // toggleRowSelection batch, and Vue's setter would add unnecessary overhead.
    this._suppressSelEmit = false
  },

  mounted () {
    this.$events.on(this.filterEvent, this.onFilterSet)
    this.$events.on(this.filterReset, this.onFilterReset)
    this.$events.on(this.refreshTable, this.refresh)
    this.fetch()
    // Re-run layout once the container has settled (fixes squished columns in
    // drawer/component-swap contexts where el-table measures too early).
    this.$nextTick(this.relayout)
    // Watch for container resize and re-layout. Guard for jsdom and older envs
    // that don't provide ResizeObserver.
    if (typeof ResizeObserver !== 'undefined') {
      var self = this
      // Read the real window width now (browser only) so responsive `hidden-*`
      // columns resolve correctly on first paint; keep it in sync on resize.
      if (typeof window !== 'undefined' && window.innerWidth) {
        this.winWidth = window.innerWidth
      }
      this._rafId = null
      this._resizeObserver = new ResizeObserver(function () {
        // Debounce via requestAnimationFrame to coalesce rapid resize events.
        if (typeof requestAnimationFrame !== 'undefined') {
          if (self._rafId !== null) cancelAnimationFrame(self._rafId)
          self._rafId = requestAnimationFrame(function () {
            self._rafId = null
            if (typeof window !== 'undefined' && window.innerWidth) {
              self.winWidth = window.innerWidth
            }
            self.relayout()
          })
        } else {
          if (typeof window !== 'undefined' && window.innerWidth) {
            self.winWidth = window.innerWidth
          }
          self.relayout()
        }
      })
      this._resizeObserver.observe(this.$el)
    }
  },

  beforeDestroy () {
    this.$events.off(this.filterEvent, this.onFilterSet)
    this.$events.off(this.filterReset, this.onFilterReset)
    this.$events.off(this.refreshTable, this.refresh)
    if (this._resizeObserver) {
      this._resizeObserver.disconnect()
      this._resizeObserver = null
    }
    if (this._rafId !== null && this._rafId !== undefined && typeof cancelAnimationFrame !== 'undefined') {
      cancelAnimationFrame(this._rafId)
      this._rafId = null
    }
  }
}
</script>

<style scoped>
.xolas-table__row-link {
  color: inherit;
  text-decoration: none;
  cursor: pointer;
}

.xolas-table__row-link:hover {
  text-decoration: underline;
  color: #409eff;
}

/* Chevron column uses Element UI's built-in
   `el-button--info el-button--mini is-plain is-round` classes (matches the
   legacy vuetable-2 expand-row button). No custom dimensions needed —
   Element UI handles spacing, color, and border via type/size/plain/round. */

/* el-table wraps header labels by default: a long single-word label plus the
   sort caret (e.g. "Spectrum") spills onto a second line, doubling header
   height. The legacy vuetable kept headers on one line. Force nowrap so column
   headers stay single-line and the header height matches the old table
   (XOL-231). Columns must be wide enough to avoid clipping. */
::v-deep .el-table th .cell {
  white-space: nowrap;
}

/* Legacy vuetable-2 parity: the old table showed a sort icon ONLY on the
   actively sorted column; el-table renders its double-caret on every sortable
   header, which (a) diverges visually from the pre-migration render and
   (b) widens/wraps narrow headers like "KM Ref". Hide the caret unless the
   column is actively sorted (Element UI sets .ascending/.descending on the th
   after a header click). Header clicks still sort — only the idle affordance
   is hidden, matching the legacy render (XOL-234 PR 8a dev-match). */
::v-deep .el-table th:not(.ascending):not(.descending) .caret-wrapper {
  display: none;
}
</style>
